/*
 * Copyright © 2018-2023, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import Params from "../common/Params";
import { ApiPromise } from "../models/ApiPromise";
import { axiosCommon, axiosPublic } from "../util/axiosSetup";
import { ClinicProfileResponse } from "../models/clinic/profile/ClinicProfileResponse";
import { ClinicProfileSettingsResponse } from "../models/clinic/profile/ClinicProfileSettingsResponse";
import { ClinicProfileSettingsRequest } from "../models/clinic/profile/ClinicProfileSettingsRequest";
import { BasicProfileSettingsRequest } from "../models/clinic/profile/BasicProfileSettingsRequest";
import { ContactsRequest } from "../models/clinic/profile/ContactsRequest";
import { CustomSectionsRequest } from "../models/clinic/profile/CustomSectionsRequest";
import { EmployeesRequest } from "../models/clinic/profile/EmployeesRequest";
import { OpeningHoursRequest } from "../models/clinic/profile/OpeningHoursRequest";
import { SpecialOpeningHoursRequest } from "../models/clinic/profile/SpecialOpeningHoursRequest";
import { ReservationTypesRequest } from "../models/clinic/profile/ReservationTypesRequest";

export default class ClinicProfileApi {
  private static baseUrl = Params.clinicServiceBaseURL;

  static getClinicProfile(clinicId: string): ApiPromise<ClinicProfileResponse> {
    const url = `${this.baseUrl}/clinics/${clinicId}/profile`;
    return axiosCommon.get(url);
  }

  static getPublicClinicProfile(clinicId: string): ApiPromise<ClinicProfileResponse> {
    const url = `${this.baseUrl}/clinics/${clinicId}/public-profile`;
    return axiosPublic.get(url);
  }

  static getClinicProfileSettings(clinicId: string): ApiPromise<ClinicProfileSettingsResponse> {
    const url = `${this.baseUrl}/clinics/${clinicId}/profile-settings`;
    return axiosCommon.get(url);
  }

  static updateClinicProfileSettings(
    clinicId: string,
    request: ClinicProfileSettingsRequest
  ): ApiPromise<ClinicProfileSettingsResponse> {
    const url = `${this.baseUrl}/clinics/${clinicId}/profile-settings`;
    return axiosCommon.post(url, request);
  }

  static updateBasicProfileSettings(
    clinicId: string,
    request: BasicProfileSettingsRequest
  ): ApiPromise<ClinicProfileSettingsResponse> {
    const url = `${this.baseUrl}/clinics/${clinicId}/profile-settings/basic`;
    return axiosCommon.post(url, request);
  }

  static updateContacts(clinicId: string, request: ContactsRequest): ApiPromise<ClinicProfileSettingsResponse> {
    const url = `${this.baseUrl}/clinics/${clinicId}/profile-settings/contacts`;
    return axiosCommon.post(url, request);
  }

  static updateCustomSections(
    clinicId: string,
    request: CustomSectionsRequest
  ): ApiPromise<ClinicProfileSettingsResponse> {
    const url = `${this.baseUrl}/clinics/${clinicId}/profile-settings/sections`;
    return axiosCommon.post(url, request);
  }

  static updateEmployees(clinicId: string, request: EmployeesRequest): ApiPromise<ClinicProfileSettingsResponse> {
    const url = `${this.baseUrl}/clinics/${clinicId}/profile-settings/employees`;
    return axiosCommon.post(url, request);
  }

  static updateOpeningHours(clinicId: string, request: OpeningHoursRequest): ApiPromise<ClinicProfileSettingsResponse> {
    const url = `${this.baseUrl}/clinics/${clinicId}/profile-settings/opening-hours`;
    return axiosCommon.post(url, request);
  }

  static updateSpecialOpeningHours(
    clinicId: string,
    request: SpecialOpeningHoursRequest
  ): ApiPromise<ClinicProfileSettingsResponse> {
    const url = `${this.baseUrl}/clinics/${clinicId}/profile-settings/special-opening-hours`;
    return axiosCommon.post(url, request);
  }

  static updateReservationTypes(
    clinicId: string,
    request: ReservationTypesRequest
  ): ApiPromise<ClinicProfileSettingsResponse> {
    const url = `${this.baseUrl}/clinics/${clinicId}/profile-settings/reservation-types`;
    return axiosCommon.post(url, request);
  }
}
